import type * as am5percent from '@amcharts/amcharts5/percent';

import {Component, NgZone, OnDestroy, OnInit, ChangeDetectionStrategy} from '@angular/core';
import {ExposureByLocationGroup, ExposureBySEGs, ExposureByTask, ExposureByTaskGroup, ExposureForSegByOrgLevel, ExposureTaskDistribution, ExposureTaskSample, WorkerExposure} from '../../models/exposureBySeg.model';
import {ExposureDetailRequest, ExposureRequest} from '../../models/exposureRequest.model';

import {ChartService} from 'src/app/services/chart.service';
import { CommonService } from '../../services/common.service';
import { DateFilterTypeEnum } from '../../enums/dateFilterType.enum';
import { Parameter } from '../../models/parameter.model';
import { ParameterService } from 'src/app/services/parameter.service';
import { Router } from '@angular/router';
import { SEG } from 'src/app/models/seg.model';
import {SegService} from '../../services/seg.service';
import { Subscription } from 'rxjs';
import { HeaderFilteringBaseComponent } from 'src/app/shared/header-filtering-base/header-filtering-base.component';

@Component({
  standalone: false,
  selector: "app-main-v2",
  templateUrl: "./main.component.html",
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ["./main.component.scss"]
})
export class MainComponent extends HeaderFilteringBaseComponent implements OnInit, OnDestroy {
  parameter: Parameter;
  workerExposures: WorkerExposure[];
  loadingWorkerExposure: boolean = true;
  showAllSEGsPopup: boolean = false;
  exposureBySEGs: ExposureBySEGs[];
  exposureBySEGsAll: ExposureBySEGs[];
  loadingExposureBySEGs: boolean = true;
  exposureDetail: ExposureForSegByOrgLevel[];
  exposureContribution: ExposureByTask[];
  associatedSegs: [];
  exposureByTaskGroupName: ExposureByTask[];
  exposureAllSEGs: SEG[];
  exposureTaskDistributions: ExposureTaskDistribution[];
  exposureTaskDistributionsNew: ExposureTaskDistribution[];
  exposureTaskSamples: ExposureTaskSample[];
  exposureByTaskGroup: ExposureByTaskGroup[];
  exposureByLocationGroup: ExposureByLocationGroup[];
  exposureByTaskGroupAll: ExposureByTaskGroup[];
  exposureByTask: ExposureByTaskGroup[];
  selectedSEG: ExposureBySEGs;
  selectedTaskGroup: ExposureByTaskGroup;
  selectedTask: ExposureByTask;
  private prevExposureRequest: ExposureRequest;
  private prevExposureByTaskGroupRequest: ExposureRequest;
  private prevExposureByLocationGroupRequest: ExposureRequest;
  loadingExposureDetail: boolean = true;
  loadingExposureContribution: boolean = true;
  loadingTaskGroup: boolean = true;
  loadingLocationGroup: boolean = true;
  loadingExposureByTaskGroup: boolean = true;
  loadingExposureTaskDistribution: boolean = true;
  loadingExposureTaskSamples: boolean = true;
  loadingAllSEGs: boolean = true;
  public showSEGDetail: boolean = false;
  public showTaskGroup: boolean = false;
  public showTaskSupervisor: boolean = false;
  segSort: string = "severity";
  segOrder: string = "asc";
  segAllSort: string = "severity";
  segAllOrder: string = "asc";
  taskRequestType: 'group' | 'task' = 'group';
  exposureByTaskChart: am5percent.PieChart;
  parameterSub: Subscription;
  organizationSub: Subscription;
  dateFilterSub: Subscription;

  constructor(
    private router: Router,
    private segService: SegService,
    private zone: NgZone,
    private chartService: ChartService,
    protected commonService: CommonService,
    private parameterService: ParameterService
  ) {
    super(commonService);
  }

  async ngOnDestroy(): Promise<void> {
    this.commonService.headerPreviousRangeSubject.next(false);
    await this.zone.runOutsideAngular(() => {
      if (this.exposureByTaskChart) this.exposureByTaskChart.dispose();
    });
    if (this.parameterSub) this.parameterSub.unsubscribe();
    if (this.organizationSub) this.organizationSub.unsubscribe();
    if (this.dateFilterSub) this.dateFilterSub.unsubscribe();
  }

  ngOnInit(): void {
    this.commonService.headerPreviousRangeSubject.next(true);
    this.loadHeaderData();
    super.ngOnInit();
  }

  loadDashboardData() {
    this.loadExposureBySEGs();
    this.filterExposureByTaskGroup();
    this.filterExposureByLocationGroup();
    this.loadWorkerExposure();
  }

  loadWorkerExposure() {
    this.workerExposures = [
      {
        name: "Low",
        color: this.commonService.colors.black.slice(1),
        workers: 0,
        percentageOfWorkers: 0.0,
        percentageOfWorkersAbs: 0,
        change: 0.0,
        changePercent: 0.0,
        changePercentAbs: 0,
      },
      {
        name: "Medium",
        color: this.commonService.colors.black.slice(1),
        workers: 0,
        percentageOfWorkers: 0.0,
        percentageOfWorkersAbs: 0,
        change: 0.0,
        changePercent: 0.0,
        changePercentAbs: 0,
      },
      {
        name: "High",
        color: this.commonService.colors.black.slice(1),
        workers: 0,
        percentageOfWorkers: 0.0,
        percentageOfWorkersAbs: 0,
        change: 0.0,
        changePercent: 0.0,
        changePercentAbs: 0,
      },
      {
        name: "Ultra-High",
        color: this.commonService.colors.black.slice(1),
        workers: 0,
        percentageOfWorkers: 0.0,
        percentageOfWorkersAbs: 0,
        change: 0.0,
        changePercent: 0.0,
        changePercentAbs: 0,
      },
    ];

    const req = {
      orgLevelId: this.selectedOrg?.id,
      analyteId: this.selectedParameter?.id,
      sampleTypes: [1, 3],
      dateFilterType:
        this.selectedDateRange?.id || this.selectedDateRange?.id == 0
          ? this.selectedDateRange?.id
          : DateFilterTypeEnum.AllDates,
      startDate: this.selectedDateRange?.start
        ? this.selectedDateRange?.start
        : 0,
      endDate: this.selectedDateRange?.end ? this.selectedDateRange?.end : 0,
      previousEndDate: this.selectedDateRange?.prevEnd
        ? this.selectedDateRange?.prevEnd
        : 0,
      previousStartDate: this.selectedDateRange?.prevStart
        ? this.selectedDateRange?.prevStart
        : 0,
    } as ExposureDetailRequest;

    this.loadingWorkerExposure = true;
    this.segService.getWorkerExposure(req).subscribe(
      (resp) => {
        resp.forEach((task) => {
          if (task?.percentageOfWorkers) {
            let percentageOfWorkers = parseFloat(
              (task.percentageOfWorkers * 100).toFixed(2)
            );
            task.percentageOfWorkersAbs = Math.abs(
              Math.round(percentageOfWorkers)
            );
          } else {
            task.percentageOfWorkersAbs = 0;
          }
          if (task?.change) {
            task.changePercent = parseFloat((task.change * 100).toFixed(2));
            task.changePercentAbs = Math.abs(Math.round(task.changePercent));
          } else {
            task.changePercent = 0;
            task.changePercentAbs = 0;
          }
        });
        this.workerExposures = resp;
        this.loadingWorkerExposure = false;
      },
      (error) => {
        this.loadingWorkerExposure = false;
      }
    );

    if (!this.selectedParameter?.id) {
      this.parameter = null;
      return;
    }

    this.parameterService.getParameterById(this.selectedParameter.id).subscribe({
      next: (parameter) => {
        this.parameter = parameter;
      },
      error: () => {
        this.parameter = null;
      },
    });
  }

  loadExposureBySEGs() {
    if (this.selectedParameter?.id) {
      const req = {
        analyteId: this.selectedParameter?.id,
        endDate: this.selectedDateRange?.end ? this.selectedDateRange?.end : 0,
        dateFilterType:
          this.selectedDateRange?.id || this.selectedDateRange?.id == 0
            ? this.selectedDateRange?.id
            : DateFilterTypeEnum.AllDates,
        previousEndDate: this.selectedDateRange?.prevEnd
          ? this.selectedDateRange?.prevEnd
          : 0,
        previousStartDate: this.selectedDateRange?.prevStart
          ? this.selectedDateRange?.prevStart
          : 0,
        startDate: this.selectedDateRange?.start
          ? this.selectedDateRange?.start
          : 0,
        orgLevelId: this.selectedOrg?.id ? this.selectedOrg?.id : 0,
      } as ExposureRequest;
      if (
        !this.prevExposureRequest ||
        JSON.stringify(this.prevExposureRequest) !== JSON.stringify(req)
      ) {
        this.prevExposureRequest = req;
        this.loadingExposureBySEGs = true;
        this.segService.getExposureBySEGs(req).subscribe(
          (exposureBySEGs) => {
            this.loadedExposureBySEGs(exposureBySEGs);
          },
          (error) => {
            this.loadedExposureBySEGs([]);
          }
        );
      }
    }
  }

  loadedExposureBySEGs(exposureBySEGs: ExposureBySEGs[]) {
    this.exposureBySEGsAll = exposureBySEGs.map(exposure => {
      return {
        ...exposure,
        change: !isNaN(exposure.change) ? exposure.change : 0,
        severity: !isNaN(exposure.severity) ? exposure.severity : 0
      }
    });
    this.exposureBySEGs = this.exposureBySEGsAll.slice(0, 5);
    this.loadingExposureBySEGs = false;
  }

  sortSEG(segSort: any) {
    if (!segSort.active || segSort.direction === "") {
      this.exposureBySEGs = this.exposureBySEGsAll;
    } else {
      this.exposureBySEGs = this.exposureBySEGsAll.sort((a, b) => {
        const aValue = (a as any)[segSort.active];
        const bValue = (b as any)[segSort.active];
        return (
          (aValue < bValue ? -1 : 1) * (segSort.direction === "asc" ? 1 : -1)
        );
      });
    }
    this.exposureBySEGs = this.exposureBySEGsAll.slice(0, 5);
  }

  showSEGDetailFn(seg: ExposureBySEGs) {
    setTimeout(() => {
      this.showSEGDetail = true;
      this.selectedSEG = seg;

      const req = {
        orgLevelId: this.selectedOrg?.id,
        segId: this.selectedSEG?.segId,
        analyteId: this.selectedParameter?.id,
        dateFilterType:
          this.selectedDateRange?.id || this.selectedDateRange?.id == 0
            ? this.selectedDateRange?.id
            : DateFilterTypeEnum.AllDates,
        sampleTypes: [1, 3],
        startDate: this.selectedDateRange?.start
          ? this.selectedDateRange?.start
          : 0,
        endDate: this.selectedDateRange?.end ? this.selectedDateRange?.end : 0,
        previousEndDate: this.selectedDateRange?.prevEnd
          ? this.selectedDateRange?.prevEnd
          : 0,
        previousStartDate: this.selectedDateRange?.prevStart
          ? this.selectedDateRange?.prevStart
          : 0,
      } as ExposureDetailRequest;

      this.loadingExposureDetail = true;
      this.segService.getExposureForSegByOrgLevel(req).subscribe(
        (exposureDetail) => {
          this.exposureDetail = exposureDetail;
          this.loadingExposureDetail = false;
        },
        (error) => {
          this.exposureDetail = [];
          this.loadingExposureDetail = false;
        }
      );

      const reqTask = {
        orgLevelId: this.selectedOrg?.id,
        segId: this.selectedSEG?.segId,
        taskGroupId: this.selectedSEG?.taskGroupId,
        analyteId: this.selectedParameter?.id,
        dateFilterType:
          this.selectedDateRange?.id || this.selectedDateRange?.id == 0
            ? this.selectedDateRange?.id
            : DateFilterTypeEnum.AllDates,
        sampleTypes: [3],
        startDate: this.selectedDateRange?.start
          ? this.selectedDateRange?.start
          : 0,
        endDate: this.selectedDateRange?.end ? this.selectedDateRange?.end : 0,
        previousEndDate: this.selectedDateRange?.prevEnd
          ? this.selectedDateRange?.prevEnd
          : 0,
        previousStartDate: this.selectedDateRange?.prevStart
          ? this.selectedDateRange?.prevStart
          : 0,
      } as ExposureDetailRequest;

      this.loadingExposureContribution = true;
      this.segService
        .getExposureByTask(reqTask)
        .subscribe((exposureContribution) => {
          exposureContribution.forEach((exposure) => {
            if (exposure?.massContribution) {
              exposure.massContributionPercent = parseFloat(
                (exposure.massContribution * 100).toFixed(2)
              );
              exposure.massContributionPercentAbs = Math.abs(
                Math.round(exposure.massContributionPercent)
              );
            } else {
              exposure.massContributionPercent = 0;
              exposure.massContributionPercentAbs = 0;
            }
            if (exposure?.timeOnShift) {
              exposure.timeOnShiftPercent = parseFloat(
                (exposure.timeOnShift * 100).toFixed(2)
              );
              exposure.timeOnShiftPercentAbs = Math.abs(
                Math.round(exposure.timeOnShiftPercent)
              );
            } else {
              exposure.timeOnShiftPercent = 0;
              exposure.timeOnShiftPercentAbs = 0;
            }
          });
          this.exposureContribution = exposureContribution;
          this.loadingExposureContribution = false;
        });

      this.taskRequestType = "group";
      this.loadExposurePieChartData();
    }, 200);
  }

  hideSEGDetailFn(event: Event) {
    if (this.checkToHidePopup(event)) {
      if (!this.showTaskSupervisor) {
        this.showSEGDetail = false;
      }
    }
  }

  showAllPopupFn() {
    setTimeout(() => {
      this.showAllSEGsPopup = true;
    }, 200);
  }

  checkToHidePopup(event: Event) {
    const elementClassList = (event.target as Element).classList;
    if (
      !elementClassList.value.includes("sort-icon") &&
      !elementClassList.value.includes("nav-link")
    ) {
      return true;
    }
  }
  hideAllSEGsPopupFn(event: Event) {
    if (this.checkToHidePopup(event)) {
      this.showAllSEGsPopup = false;
    }
  }

  toggleTaskwithSEGPopup(task: ExposureByTask) {
    setTimeout(() => {
      this.showTaskSupervisor = true;
      this.selectedTask = task;
      this.exposureAllSEGs = [];
      this.segService
        .getAllSegsForTaskGroup(task.taskGroupId)
        .subscribe((allSegs) => {
          this.exposureAllSEGs = allSegs;
          this.loadingAllSEGs = false;
        });

      const req = {
        orgLevelId: this.selectedOrg?.id,
        analyteId: this.selectedParameter?.id,
        taskId: this.selectedTask?.taskId,
        segId: this.selectedSEG?.segId,
        sampleTypes: [3],
        dateFilterType:
          this.selectedDateRange?.id || this.selectedDateRange?.id == 0
            ? this.selectedDateRange?.id
            : DateFilterTypeEnum.AllDates,
        startDate: this.selectedDateRange?.start
          ? this.selectedDateRange?.start
          : 0,
        endDate: this.selectedDateRange?.end ? this.selectedDateRange?.end : 0,
        previousEndDate: this.selectedDateRange?.prevEnd
          ? this.selectedDateRange?.prevEnd
          : 0,
        previousStartDate: this.selectedDateRange?.prevStart
          ? this.selectedDateRange?.prevStart
          : 0,
      } as ExposureDetailRequest;

      this.loadingExposureTaskDistribution = true;
      this.segService.getExposureForTaskByOrgLevel(req).subscribe(
        (resp) => {
          this.exposureTaskDistributions = [];
          resp.forEach((exposure) => {
            if (exposure?.massContribution) {
              exposure.massContributionPercent = parseFloat(
                (exposure.massContribution * 100).toFixed(2)
              );
              exposure.massContributionPercentAbs = Math.abs(
                Math.round(exposure.massContributionPercent)
              );
            } else {
              exposure.massContributionPercent = 0;
              exposure.massContributionPercentAbs = 0;
            }
            this.exposureTaskDistributions.push(exposure);
          });
          this.loadingExposureTaskDistribution = false;
        },
        (error) => {
          this.exposureTaskDistributions = [];
          this.loadingExposureTaskDistribution = false;
        }
      );

      this.loadingExposureTaskSamples = true;
      this.segService.getSampleExposureForTaskByOrgLevel(req).subscribe(
        (resp) => {
          this.exposureTaskSamples = [];
          resp.forEach((exposure) => {
            if (exposure?.taskContribution) {
              exposure.taskContributionPercent = parseFloat(
                (exposure.taskContribution * 100).toFixed(2)
              );
              exposure.taskContributionPercentAbs = Math.abs(
                Math.round(exposure.taskContributionPercent)
              );
            } else {
              exposure.taskContributionPercent = 0;
              exposure.taskContributionPercentAbs = 0;
            }
            if (exposure.timeOnShift) {
              exposure.timeOnShiftMins = Math.round(exposure.timeOnShift / 60);
            } else {
              exposure.timeOnShiftMins = 0;
            }
            this.exposureTaskSamples.push(exposure);
          });
          this.loadingExposureTaskSamples = false;
        },
        (error) => {
          this.exposureTaskSamples = [];
          this.loadingExposureTaskSamples = false;
        }
      );
    }, 300);
  }

  toggleTaskRequest() {
    if (this.taskRequestType === 'group') this.taskRequestType = 'task';
    else this.taskRequestType = 'group';
    this.loadExposurePieChartData();
  }

  hideTaskSupervisorFn(event: Event) {
    if (this.checkToHidePopup(event)) {
      setTimeout(() => {
        this.showTaskSupervisor = false;
      }, 100);
    }
  }

  hideTaskGroupFn(event: Event) {
    if (this.checkToHidePopup(event)) {
      if (!this.showTaskSupervisor) {
        this.showTaskGroup = false;
      }
    }
  }

  toggleTaskPopup(task: ExposureByTask) {
    setTimeout(() => {
      this.showTaskSupervisor = true;
      this.selectedSEG = null;
      this.selectedTask = task;
      const req = {
        orgLevelId: this.selectedOrg?.id,
        analyteId: this.selectedParameter?.id,
        taskId: this.selectedTask?.taskId,
        sampleTypes: [3],
        dateFilterType:
          this.selectedDateRange?.id || this.selectedDateRange?.id == 0
            ? this.selectedDateRange?.id
            : DateFilterTypeEnum.AllDates,
        startDate: this.selectedDateRange?.start
          ? this.selectedDateRange?.start
          : 0,
        endDate: this.selectedDateRange?.end ? this.selectedDateRange?.end : 0,
        previousEndDate: this.selectedDateRange?.prevEnd
          ? this.selectedDateRange?.prevEnd
          : 0,
        previousStartDate: this.selectedDateRange?.prevStart
          ? this.selectedDateRange?.prevStart
          : 0,
      } as ExposureDetailRequest;
      this.loadingExposureTaskDistribution = true;
      this.segService.getExposureForTaskByOrgLevel(req).subscribe((resp) => {
        this.exposureTaskDistributions = [];
        resp.forEach((exposure) => {
          if (exposure?.massContribution) {
            exposure.massContributionPercent = parseFloat(
              (exposure.massContribution * 100).toFixed(2)
            );
            exposure.massContributionPercentAbs = Math.abs(
              Math.round(exposure.massContributionPercent)
            );
          } else {
            exposure.massContributionPercent = 0;
            exposure.massContributionPercentAbs = 0;
          }
          this.exposureTaskDistributions.push(exposure);
        });
        this.loadingExposureTaskDistribution = false;
      });
      this.loadingExposureTaskSamples = true;
      this.segService
        .getSampleExposureForTaskByOrgLevel(req)
        .subscribe((resp) => {
          this.exposureTaskSamples = [];
          resp.forEach(
            (exposure) => {
              if (exposure?.taskContribution) {
                exposure.taskContributionPercent = parseFloat(
                  (exposure.taskContribution * 100).toFixed(2)
                );
                exposure.taskContributionPercentAbs = Math.abs(
                  Math.round(exposure.taskContributionPercent)
                );
              } else {
                exposure.taskContributionPercent = 0;
                exposure.taskContributionPercentAbs = 0;
              }
              if (exposure.timeOnShift) {
                exposure.timeOnShiftMins = Math.round(
                  exposure.timeOnShift / 60
                );
              } else {
                exposure.timeOnShiftMins = 0;
              }
              this.exposureTaskSamples.push(exposure);
            },
            (error: unknown) => {
              this.exposureTaskSamples = [];
              this.loadingExposureTaskSamples = false;
            }
          );
          this.loadingExposureTaskSamples = false;
        });
    }, 300);
  }

  toggleTaskGroupPopupFn(task: ExposureByTaskGroup) {
    setTimeout(() => {
      if (task?.taskGroupName) {
        this.showTaskGroup = true;
        this.selectedTaskGroup = task;
        this.loadingExposureTaskDistribution = true;

        const req = {
          orgLevelId: this.selectedOrg?.id,
          analyteId: this.selectedParameter?.id,
          taskGroupId: task.taskGroupId,
          /* segId: this.selectedSEG?.segId,*/
          sampleTypes: [3],
          dateFilterType:
            this.selectedDateRange?.id || this.selectedDateRange?.id == 0
              ? this.selectedDateRange?.id
              : DateFilterTypeEnum.AllDates,
          startDate: this.selectedDateRange?.start
            ? this.selectedDateRange?.start
            : 0,
          endDate: this.selectedDateRange?.end
            ? this.selectedDateRange?.end
            : 0,
          previousEndDate: this.selectedDateRange?.prevEnd
            ? this.selectedDateRange?.prevEnd
            : 0,
          previousStartDate: this.selectedDateRange?.prevStart
            ? this.selectedDateRange?.prevStart
            : 0,
        } as ExposureDetailRequest;

        this.exposureTaskDistributionsNew = [];
        this.segService
          .getExposureForTaskGroupByOrgLevel(req)
          .subscribe((resp) => {
            resp.forEach((exposure) => {
              if (exposure?.massContribution) {
                exposure.massContributionPercent = parseFloat(
                  (exposure.massContribution * 100).toFixed(2)
                );
                exposure.massContributionPercentAbs = Math.abs(
                  Math.round(exposure.massContributionPercent)
                );
              } else {
                exposure.massContributionPercent = 0;
                exposure.massContributionPercentAbs = 0;
              }

              this.exposureTaskDistributionsNew.push(exposure);
            });
            this.loadingExposureTaskDistribution = false;
          });

        this.loadingAllSEGs = true;
        this.exposureAllSEGs = [];
        this.segService
          .getAllSegsForTaskGroup(task.taskGroupId)
          .subscribe((allSegs) => {
            this.exposureAllSEGs = allSegs;
            this.loadingAllSEGs = false;
          });

        const exposureByTaskGroupNameRequest = {
          orgLevelId: this.selectedOrg?.id,
          analyteId: this.selectedParameter?.id,
          taskGroupId: task.taskGroupId,
          sampleTypes: [3],
          dateFilterType:
            this.selectedDateRange?.id || this.selectedDateRange?.id == 0
              ? this.selectedDateRange?.id
              : DateFilterTypeEnum.AllDates,
          startDate: this.selectedDateRange?.start
            ? this.selectedDateRange?.start
            : 0,
          endDate: this.selectedDateRange?.end
            ? this.selectedDateRange?.end
            : 0,
          previousEndDate: this.selectedDateRange?.prevEnd
            ? this.selectedDateRange?.prevEnd
            : 0,
          previousStartDate: this.selectedDateRange?.prevStart
            ? this.selectedDateRange?.prevStart
            : 0,
        } as ExposureDetailRequest;
        this.loadingExposureByTaskGroup = true;

        this.segService
          .getExposureByTask(exposureByTaskGroupNameRequest)
          .subscribe((exposureByTaskGroupName) => {
            this.exposureByTaskGroupName = [];
            exposureByTaskGroupName.forEach((exposure) => {
              if (
                exposure.taskGroupName == this.selectedTaskGroup.taskGroupName
              ) {
                if (exposure?.massContribution) {
                  exposure.massContributionPercent = parseFloat(
                    (exposure.massContribution * 100).toFixed(2)
                  );
                  exposure.massContributionPercentAbs = Math.abs(
                    Math.round(exposure.massContributionPercent)
                  );
                } else {
                  exposure.massContributionPercent = 0;
                  exposure.massContributionPercentAbs = 0;
                }
                if (exposure?.timeOnShift) {
                  exposure.timeOnShiftPercent = parseFloat(
                    (exposure.timeOnShift * 100).toFixed(2)
                  );
                  exposure.timeOnShiftPercentAbs = Math.abs(
                    Math.round(exposure.timeOnShiftPercent)
                  );
                } else {
                  exposure.timeOnShiftPercent = 0;
                  exposure.timeOnShiftPercentAbs = 0;
                }
                this.exposureByTaskGroupName.push(exposure);
              }
            });
            this.loadingExposureByTaskGroup = false;
          });
      }
    }, 200);
  }

  loadExposurePieChartData() {
    const exposureByTaskRequest = {
      analyteId: this.selectedParameter?.id,
      sampleTypes: [3],
      endDate: this.selectedDateRange?.end ? this.selectedDateRange?.end : 0,
      dateFilterType:
        this.selectedDateRange?.id || this.selectedDateRange?.id == 0
          ? this.selectedDateRange?.id
          : DateFilterTypeEnum.AllDates,
      previousEndDate: this.selectedDateRange?.prevEnd
        ? this.selectedDateRange?.prevEnd
        : 0,
      previousStartDate: this.selectedDateRange?.prevStart
        ? this.selectedDateRange?.prevStart
        : 0,
      segId: this.selectedSEG?.segId,
      startDate: this.selectedDateRange?.start
        ? this.selectedDateRange?.start
        : 0,
      orgLevelId: this.selectedOrg?.id ? this.selectedOrg?.id : 0,
    } as ExposureDetailRequest;
    let chartDiv = document.getElementById("taskExposureChart");
    chartDiv.innerHTML =
      '<div style="text-align: center;"><img src="assets/images/loader.gif"></div>';
    let taskRequest = null;
    if (this.taskRequestType == "group")
      taskRequest = this.segService.getExposureByTaskGroup(
        exposureByTaskRequest
      );
    else taskRequest = this.segService.getExposureByTask(exposureByTaskRequest);
    taskRequest.subscribe(async (exposureByTask) => {
      exposureByTask.forEach((task) => {
        if (task?.massContribution) {
          task.massContributionPercent = parseFloat(
            (task.massContribution * 100).toFixed(2)
          );
        } else {
          task.massContributionPercent = 0;
        }
        if (!task?.taskGroupName) task.taskGroupName = "";
      });
      this.exposureByTask = exposureByTask;
      if (this.exposureByTask.length) {
        await this.zone.runOutsideAngular(() => {
          setTimeout(async () => {
            await this.chartService.setDashboardExposureByTaskChart(
              this.exposureByTaskChart,
              "taskExposureChart",
              this.exposureByTask,
              this.taskRequestType
            );
          }, 1000);
        });
      } else {
        await this.zone.runOutsideAngular(() => {
          if (this.exposureByTaskChart) this.exposureByTaskChart.dispose();
        });
        chartDiv.innerHTML = '<div style="text-align: center;"></div>';
      }
    });
  }

  private filterExposureByLocationGroup() {
    if (this.selectedParameter?.id) {
      const req = {
        analyteId: this.selectedParameter?.id,
        endDate: this.selectedDateRange?.end ? this.selectedDateRange?.end : 0,
        dateFilterType:
          this.selectedDateRange?.id || this.selectedDateRange?.id == 0
            ? this.selectedDateRange?.id
            : DateFilterTypeEnum.AllDates,
        previousEndDate: this.selectedDateRange?.prevEnd
          ? this.selectedDateRange?.prevEnd
          : 0,
        previousStartDate: this.selectedDateRange?.prevStart
          ? this.selectedDateRange?.prevStart
          : 0,
        sampleTypes: [3],
        startDate: this.selectedDateRange?.start
          ? this.selectedDateRange?.start
          : 0,
        orgLevelId: this.selectedOrg?.id ? this.selectedOrg?.id : 0,
      } as ExposureDetailRequest;
      if (
        !this.prevExposureByLocationGroupRequest ||
        JSON.stringify(this.prevExposureByLocationGroupRequest) !==
          JSON.stringify(req)
      ) {
        this.prevExposureByLocationGroupRequest = req;
        this.loadingLocationGroup = true;
        this.segService.getExposureContributionByLocationGroup(req).subscribe(
          (resp) => {
            resp.forEach((task) => {
              if (task?.massContribution) {
                task.massContributionPercent = parseFloat(
                  (task.massContribution * 100).toFixed(2)
                );
                task.massContributionPercentAbs = Math.abs(
                  Math.round(task.massContributionPercent)
                );
              } else {
                task.massContributionPercent = 0;
                task.massContributionPercentAbs = 0;
              }
            });
            this.exposureByLocationGroup = resp;
            this.loadingLocationGroup = false;
          },
          (error) => {
            this.exposureByLocationGroup = [];
            this.loadingLocationGroup = false;
          }
        );
      }
    }
  }

  private filterExposureByTaskGroup() {
    if (this.selectedParameter?.id) {
      const req = {
        analyteId: this.selectedParameter?.id,
        sampleTypes: [3],
        endDate: this.selectedDateRange?.end ? this.selectedDateRange?.end : 0,
        dateFilterType:
          this.selectedDateRange?.id || this.selectedDateRange?.id == 0
            ? this.selectedDateRange?.id
            : DateFilterTypeEnum.AllDates,
        previousEndDate: this.selectedDateRange?.prevEnd
          ? this.selectedDateRange?.prevEnd
          : 0,
        previousStartDate: this.selectedDateRange?.prevStart
          ? this.selectedDateRange?.prevStart
          : 0,
        segId: null,
        startDate: this.selectedDateRange?.start
          ? this.selectedDateRange?.start
          : 0,
        orgLevelId: this.selectedOrg?.id ? this.selectedOrg?.id : 0,
      } as ExposureDetailRequest;
      if (
        !this.prevExposureByTaskGroupRequest ||
        JSON.stringify(this.prevExposureByTaskGroupRequest) !==
          JSON.stringify(req)
      ) {
        this.prevExposureByTaskGroupRequest = req;
        this.loadingTaskGroup = true;
        this.segService.getExposureByTaskGroup(req).subscribe(
          (exposureByTaskGroup) => {
            exposureByTaskGroup.forEach((task) => {
              if (task?.massContribution) {
                task.massContributionPercent = parseFloat(
                  (task.massContribution * 100).toFixed(2)
                );
                task.massContributionPercentAbs = Math.abs(
                  Math.round(task.massContributionPercent)
                );
              } else {
                task.massContributionPercent = 0;
                task.massContributionPercentAbs = 0;
              }
            });
            this.exposureByTaskGroupAll = exposureByTaskGroup;
            this.exposureByTaskGroup = this.exposureByTaskGroupAll.slice(0, 5);
            this.loadingTaskGroup = false;
          },
          (error) => {
            this.exposureByTaskGroupAll = [];
            this.exposureByTaskGroup = [];
            this.loadingTaskGroup = false;
          }
        );
      }
    }
  }

  changeAbs(val) {
    return Math.abs(val);
  }

  redirectToSample(sensorAssignmentId) {
    this.router.navigate(["/dashboard/sample-details/" + sensorAssignmentId]);
  }

  get hasDateFilter() {
    return true;
  }

  get hasOrgFilter() {
    return true;
  }

  get hasParameterFilter() {
    return true;
  }

  loadComponentData() {
    this.loadDashboardData();
  }

  updateExposureContribution(data) {
    this.exposureContribution = data;
  }
}
